import { Injectable } from "@angular/core";
import { ExportContext, ExportFileFormat, ExportRange, ExportService } from "app/layout/export-overlay";
import { get } from "lodash-es";
import { IPagination, IPaginationResult, IPaginationResult2, IPaginationResultFrequencies, IPaginationResultPagesList } from "app/common/interfaces";
import { takeUntil } from "rxjs";
import ExpenseAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/process/expense/expense.entity";
import { CREDIT_APPLICATION_PROPERTIES, CREDIT_APPLICATION_STATUS_TEXTS, CURRENCYFIELDS, DATE_FIELDS, DATE_TIME_FORMATTER, FORMATTER } from "./credit-application.constants";
import { CreditRequestFilters } from "app/modules/components/reports/credit-application/interfaces/credit-request.model";
import { CreditApplicationAttributes } from "@albatrosdeveloper/ave-models-package";
import { CreditRequestService } from "app/modules/components/reports/credit-application/credit-application.component.service";

export interface ExportData {
    id: number,
    username: string,
    businessPartner: string,
    creationDateTime: string,
    amountCreditRequested: number,
    amountCreditApproved: number,
    approvalDate: string,
    approvalUser: string,
    reasonRejection: string,
    statusId: string
}

export interface CreditApplicationExport extends CreditApplicationAttributes {
    creationDateTime: Date;
}

@Injectable()
export class CreditApplicationReportExport {
    constructor(
        private exportService: ExportService,
        private readonly creditRequestService: CreditRequestService
    ) { }

    getStatusText(status: number): string {
        return CREDIT_APPLICATION_STATUS_TEXTS[status] || "Desconocido";
    }


    private transformData(lossList: CreditApplicationExport[]): ExportData[] {
        return lossList?.flatMap((item: CreditApplicationExport) => ({
            id: get(item, "id", 0),
            username: get(item, "user.first_name", "") || get(item, "user.username", ""),
            businessPartner: get(item, "businessPartner.name", "") ,
            creationDateTime: item.creationDateTime as unknown as string,
            amountCreditRequested: get(item, "amountCreditRequested", 0),
            amountCreditApproved: get(item, "amountCreditApproved", 0),
            approvalDate: item.approvalDate as unknown as string,
            approvalUser: get(item, "approvalUser.username", ""),
            reasonRejection: get(item, "reasonRejection.name", ""),
            statusId: this.getStatusText(item.statusId),
        }));
    }

    export(
        format: ExportFileFormat,
        range: ExportRange,
        totalItems: number,
        pageSize: number,
        pageIndex: number,
        exportId: string,
        exportTitle: string,
        properties: typeof CREDIT_APPLICATION_PROPERTIES,
        filters: CreditRequestFilters,
        optionsFilters: IPagination<CreditApplicationAttributes>
    ) {
        this.exportService
            .task({
                id: exportId,
                title: exportTitle,
                format,
                iterations: 1,
                start: (ctx: ExportContext) => {
                    ctx.processor.localizeItemProperties(
                        exportTitle,
                        properties ?? CREDIT_APPLICATION_PROPERTIES
                    );
                },
                next: (ctx: ExportContext) => {
                    optionsFilters.limitPerPage = range === ExportRange.AllPages ? totalItems : pageSize;
                    optionsFilters.currentPage = range === ExportRange.AllPages ? 1 : pageIndex;

                    this.creditRequestService
                        .findAllWithCount(
                            optionsFilters,
                            filters
                        )
                        .pipe(takeUntil(ctx.stopped))
                        .subscribe({
                            next: (res: IPaginationResultFrequencies<CreditApplicationExport>) => {
                                const formattedData = res?.data.data.flatMap((item: CreditApplicationExport) => {

                                    DATE_FIELDS.forEach((field) => {
                                        const dateValue = item[field as keyof CreditApplicationExport];
                                        if (typeof dateValue === "string" && !isNaN(Date.parse(dateValue))) {
                                            const parsedDate = new Date(dateValue);
                                            (item as any)[field] = DATE_TIME_FORMATTER.format(parsedDate);
                                        }
                                    });

                                    const itemForExport = {
                                        ...item,
                                    };

                                    return itemForExport;
                                });

                                const responseData = formattedData;
                                const rows = this.transformData(responseData);

                                ctx.processor.addToSection(exportTitle, rows);
                                ctx.next();
                            },
                        });
                },
            })
            .start();
    }

    canStartTask(exportId: string): boolean {
        if (this.exportService.taskIsActive(exportId)) {
            this.exportService.setVisible(true);
            return false;
        }
        return true;
    }
}